import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** slug -> activity id cache so repeated views don't re-query. */
const slugIdCache = new Map<string, string | null>();

/**
 * Resolve an activity id from a path like `/activity/<uuid>` or `/activity/<slug>`.
 * Returns null for any other path.
 */
export const resolveActivityIdFromPath = async (path: string): Promise<string | null> => {
  const match = path.match(/^\/activity\/([^/?#]+)/);
  if (!match) return null;
  const key = decodeURIComponent(match[1]);
  if (UUID_RE.test(key)) return key;
  if (slugIdCache.has(key)) return slugIdCache.get(key) ?? null;
  const { data } = await supabase
    .from("activities")
    .select("id")
    .eq("slug", key)
    .maybeSingle();
  const id = data?.id ?? null;
  slugIdCache.set(key, id);
  return id;
};

/**
 * Fire-and-forget event tracking. Inserts into page_events table.
 * Does not block UI — errors are silently logged.
 */
export const trackEvent = (
  eventType: string,
  eventName: string,
  options?: {
    pagePath?: string;
    activityId?: string;
    metadata?: Record<string, unknown>;
  }
) => {
  const record: Record<string, unknown> = {
    event_type: eventType,
    event_name: eventName,
    page_path: options?.pagePath ?? (typeof window !== "undefined" ? window.location.pathname : null),
  };
  if (options?.activityId) record.activity_id = options.activityId;
  if (options?.metadata) record.metadata = options.metadata;

  // Try to attach current user id (may be null for anonymous)
  supabase.auth.getUser().then(({ data }) => {
    if (data?.user?.id) record.user_id = data.user.id;
    supabase.from("page_events").insert(record as any).then(({ error }) => {
      if (error) console.error("Track event error:", error.message);
    });
  });
};

/** Last activity view, used to drop the duplicate id -> slug redirect view. */
let lastActivityView: { id: string; at: number } | null = null;

/**
 * Track a page view, attaching the activity id whenever the path is an
 * activity/class/cafe detail page so per-listing view stats work.
 */
export const trackPageView = (pagePath: string) => {
  resolveActivityIdFromPath(pagePath)
    .then((activityId) => {
      if (activityId) {
        // Detail pages redirect /activity/<uuid> -> /activity/<slug>; count one view.
        const now = Date.now();
        if (lastActivityView && lastActivityView.id === activityId && now - lastActivityView.at < 10000) return;
        lastActivityView = { id: activityId, at: now };
      }
      trackEvent("page_view", "page_view", { pagePath, activityId: activityId ?? undefined });
    })
    .catch(() => trackEvent("page_view", "page_view", { pagePath }));
};

/**
 * Track a tap on an activity card, recording where in the page it sat so we can
 * see which rows and positions earn clicks.
 */
export const trackCardClick = (
  activityId: string,
  context?: { surface?: string; row?: string; position?: number }
) => {
  trackEvent("button_click", "card_click", {
    activityId,
    metadata: {
      surface: context?.surface ?? null,
      row: context?.row ?? null,
      position: context?.position ?? null,
    },
  });
};

export const useTrackEvent = () => {
  const track = useCallback(
    (eventType: string, eventName: string, activityId?: string) => {
      trackEvent(eventType, eventName, { activityId });
    },
    []
  );
  return { trackEvent: track, trackPageView };
};
